import { Divider } from 'antd'
import { cva } from 'class-variance-authority'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import ArrowDownIcon from '@/assets/icons/arrow-down.svg?react'
import CheckIcon from '@/assets/icons/check.svg?react'
import { cn } from '@/utils/style'

interface DropdownOption {
  label: string
  className?: string
  value: string | number
  disabled?: boolean
  separatorBefore?: boolean
}

interface DropdownProps {
  options: DropdownOption[]
  value?: string | number | (string | number)[]
  disabled?: boolean
  allowClear?: boolean
  onChange?: (value: string | number | (string | number)[]) => void
  onClear?: () => void
  placeholder?: string
  rootClassName?: string
  className?: string
  menuClassName?: string
  dropup?: boolean
  placement?: 'left' | 'right'
  multiple?: boolean
  showAllOption?: boolean
}

const dropdownVariants = cva(
  'w-full h-11 px-4 rounded-lg border-[2px] border-gray00 outline-none relative flex items-center transition-colors',
  {
    variants: {
      open: {
        true: 'border-secondary',
        false: 'border-gray00',
      },
      disabled: {
        true: 'cursor-not-allowed bg-gray-100',
      },
    },
  },
)

const ALL_OPTION_VALUE = 'all'

export function Dropdown({
  options,
  value,
  disabled = false,
  allowClear = false,
  onChange,
  onClear,
  placeholder = 'Select an option',
  rootClassName,
  className,
  menuClassName,
  dropup = false,
  placement = 'right',
  multiple = false,
  showAllOption = false,
}: DropdownProps) {
  const [internalValue, setInternalValue] = useState<string | number | (string | number)[]>(() => {
    if (value !== undefined) return value
    if (multiple) {
      return showAllOption ? [ALL_OPTION_VALUE] : []
    }
    return options[0]?.value
  })

  const dropdownRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)

  const allOption: DropdownOption = useMemo(() => ({ label: 'All', value: ALL_OPTION_VALUE }), [])

  const displayedOptions = useMemo(() => {
    return showAllOption ? [allOption, ...options] : options
  }, [options, showAllOption, allOption])

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value)
    } else {
      const defaultValue = multiple ? (showAllOption ? [ALL_OPTION_VALUE] : []) : options[0]?.value
      setInternalValue(defaultValue)
    }
  }, [value, multiple, options, showAllOption])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleOptionClick = useCallback(
    (option: DropdownOption) => {
      if (option.disabled) return

      let newValue: (string | number)[] | string | number

      if (multiple) {
        const currentSelected = Array.isArray(internalValue) ? [...internalValue] : []
        let newSelected: (string | number)[]

        if (option.value === ALL_OPTION_VALUE) {
          newSelected = [ALL_OPTION_VALUE]
        } else {
          const updatedSelection = currentSelected.filter((v) => v !== ALL_OPTION_VALUE)
          const index = updatedSelection.indexOf(option.value)

          if (index > -1) {
            updatedSelection.splice(index, 1)
          } else {
            updatedSelection.push(option.value)
          }

          if (updatedSelection.length === options.length) {
            newSelected = [ALL_OPTION_VALUE]
          } else {
            newSelected = updatedSelection
          }
        }
        newValue = newSelected
      } else {
        newValue = option.value
        setIsOpen(false)
      }

      setInternalValue(newValue)
      if (onChange) {
        onChange(newValue)
      }
    },
    [multiple, internalValue, options.length, onChange],
  )

  const handleClear = useCallback(() => {
    const newValue = multiple ? (showAllOption ? [ALL_OPTION_VALUE] : []) : options[0]?.value
    setInternalValue(newValue)
    if (onClear) onClear()
    if (onChange) onChange(newValue)
  }, [multiple, showAllOption, options, onClear, onChange])

  const getDisplayLabel = () => {
    if (!multiple) {
      return options.find((opt) => opt.value === internalValue)?.label || placeholder
    }

    const selectedValues = internalValue as (string | number)[]
    if (selectedValues.includes(ALL_OPTION_VALUE)) {
      return allOption.label
    }
    if (selectedValues.length === 0) {
      return placeholder
    }
    if (selectedValues.length >= 2) {
      return (
        <div className="bg-blue-50 text-highlight rounded-full t-sb w-6 h-6 flex items-center justify-center">
          {selectedValues.length}
        </div>
      )
    }
    const singleSelectedLabel = options.find((opt) => opt.value === selectedValues[0])?.label
    return singleSelectedLabel || ''
  }

  const hasValue = useMemo(() => {
    if (!multiple) {
      return (
        internalValue !== undefined && options.length > 0 && internalValue !== options[0]?.value
      )
    }
    const selected = internalValue as (string | number)[]
    return selected.length > 0 && (selected.length > 1 || !selected.includes(ALL_OPTION_VALUE))
  }, [internalValue, multiple, options])

  return (
    <div ref={dropdownRef} className={cn('relative', rootClassName)}>
      <div
        className={cn(dropdownVariants({ open: isOpen, disabled }), className)}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
      >
        <span className={cn('flex-1 truncate', hasValue ? 't-sb' : 't-s-sb')}>
          {getDisplayLabel()}
        </span>
        {allowClear && !disabled && hasValue && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleClear()
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
        <ArrowDownIcon
          className={cn('w-5 h-5 text-gray-400 ml-2 transition-transform', {
            'rotate-180': isOpen,
          })}
        />
      </div>
      {isOpen && (
        <ul
          className={cn(
            'absolute z-10 w-full bg-white rounded-lg overflow-auto p-1 shadow-[0px_4px_12px_0px_#21336504,0px_8px_32px_0px_#21336506,0px_20px_72px_0px_#21336508,0px_64px_240px_0px_#2133650D]',
            dropup ? 'bottom-full mb-1' : 'top-full mt-1',
            placement === 'right' ? 'right-0' : 'left-0',
            menuClassName,
          )}
        >
          {displayedOptions.map((option, index) => {
            const isSelected = multiple
              ? Array.isArray(internalValue) && internalValue.includes(option.value)
              : internalValue === option.value

            return (
              <React.Fragment key={option.value}>
                {option.separatorBefore && index !== 0 && <Divider className="my-1" />}
                <li
                  className={cn(
                    'px-3 py-2 hover:bg-[#C1F7E4] cursor-pointer rounded flex items-center justify-between',
                    isSelected ? 't-sb' : 't-s-sb',
                    option.disabled && 'text-gray-400 cursor-not-allowed',
                    multiple && isSelected && 'bg-[#F1FCF8]',
                    option.className,
                  )}
                  onClick={() => handleOptionClick(option)}
                >
                  {option.label}
                  {multiple && isSelected && <CheckIcon className="w-4 h-4 text-highlight" />}
                </li>
                {showAllOption && option.value === ALL_OPTION_VALUE && <Divider className="my-1" />}
              </React.Fragment>
            )
          })}
        </ul>
      )}
    </div>
  )
}
